/**
 * Which photograph goes on which page.
 *
 * Both live in the generic `settings` table: `hero` is the portrait beside
 * the homepage headline, `aboutPhoto` the one on „O mnie”. They are separate
 * settings so Diana can use a different picture on each — but the About page
 * follows the homepage by default, so it is never blank just because she
 * uploaded a photo only once.
 */

export type SitePhoto = { mediaId?: string; url?: string; alt?: string }

/**
 * `hero` — the same picture as the homepage, whatever that is today.
 * `own`  — its own picture, carried in the same fields as `hero`.
 * `none` — deliberately no picture, even though the homepage has one.
 */
export type AboutPhotoMode = 'hero' | 'own' | 'none'

export type AboutPhotoSetting = SitePhoto & { mode?: AboutPhotoMode }

export const ABOUT_PHOTO_KEY = 'aboutPhoto'

/**
 * A missing `mode` is read as `own` when a picture is stored and `hero`
 * otherwise, so the setting keeps working if it is ever written with the
 * plain `hero`-shaped value.
 */
export function aboutPhotoMode(setting: AboutPhotoSetting | undefined): AboutPhotoMode {
  return setting?.mode ?? (setting?.url ? 'own' : 'hero')
}

/** What the public „O mnie” page should render. Empty object = no photo. */
export function resolveAboutPhoto(settings: Record<string, unknown>): SitePhoto {
  const hero = (settings.hero ?? {}) as SitePhoto
  const about = (settings[ABOUT_PHOTO_KEY] ?? {}) as AboutPhotoSetting

  switch (aboutPhotoMode(about)) {
    case 'none':
      return {}
    case 'own':
      return { mediaId: about.mediaId, url: about.url, alt: about.alt }
    default:
      return hero
  }
}
